import axios from 'axios';
import { transliterate } from '../../utils/translit';
import { isValidArtwork } from '../../utils/stringCleaners';

/**
 * Last.FM API Provider
 */

const SEARCH_TIMEOUT = 20000;
const LASTFM_API_KEY = process.env.REACT_APP_LASTFM_API_KEY || '406f1bf8172d1d4d0c68b3a11429a3b2';

/**
 * Optimize Last.FM image URL to get higher resolution
 * @param {string} url - Original URL
 * @returns {string|null} - Optimized URL
 */
export const optimizeLastFMUrl = (url) => {
    if (!url) return null;
    return url
        .replace(/\/300x300\//i, '/600x600/')
        .replace(/\/174s\//i, '/600x600/')
        .replace(/\/extralarge\//i, '/600x600/')
        .replace(/\/mega\//i, '/600x600/');
};

/**
 * Search Last.FM for track artwork
 * @param {string} cleanArtist - Cleaned artist name
 * @param {string} cleanTrack - Cleaned track name
 * @returns {Promise<string|null>} - Artwork URL or null
 */
export const searchLastFMTrack = async (cleanArtist, cleanTrack) => {
    try {
        console.log(`[Last.FM] Searching track: "${cleanArtist}" - "${cleanTrack}"`);
        const response = await axios.get('https://ws.audioscrobbler.com/2.0/', {
            params: {
                method: 'track.getInfo',
                artist: cleanArtist,
                track: cleanTrack,
                api_key: LASTFM_API_KEY,
                format: 'json'
            },
            timeout: SEARCH_TIMEOUT
        });

        const track = response.data.track;
        if (track && track.album && track.album.image) {
            const images = track.album.image;
            const targetImg = images.find(i => i.size === 'extralarge') || images.find(i => i.size === 'large') || images[images.length - 1];
            if (targetImg && targetImg['#text'] && isValidArtwork(targetImg['#text'])) {
                const url = optimizeLastFMUrl(targetImg['#text']);
                console.log(`[Last.FM Track] Found: ${url}`);
                return url;
            }
        }
    } catch (e) {
        console.warn('[Last.FM Track] Error:', e);
    }
    return null;
};

/**
 * Search Last.FM for artist photo
 * @param {string} cleanArtist - Cleaned artist name
 * @returns {Promise<string|null>} - Artwork URL or null
 */
export const searchLastFM = async (cleanArtist) => {
    try {
        console.log(`[Last.FM] Searching artist: "${cleanArtist}"`);
        const response = await axios.get('https://ws.audioscrobbler.com/2.0/', {
            params: {
                method: 'artist.getinfo',
                artist: cleanArtist,
                api_key: LASTFM_API_KEY,
                format: 'json'
            },
            timeout: SEARCH_TIMEOUT
        });

        const artist = response.data.artist;
        if (artist && artist.image) {
            const images = artist.image;
            const targetImg = images.find(i => i.size === 'mega') || images.find(i => i.size === 'extralarge') || images[images.length - 1];

            if (targetImg && targetImg['#text']) {
                const url = optimizeLastFMUrl(targetImg['#text']);
                if (isValidArtwork(url)) {
                    console.log(`[Last.FM Artist] Found: ${url}`);
                    return url;
                }
            }
        }
    } catch (e) {
        console.warn('[Last.FM Artist] Error:', e);
    }

    // Try transliterated name
    const transArtist = transliterate(cleanArtist);
    if (transArtist !== cleanArtist) {
        console.log(`[Last.FM] Retrying with transliteration: "${transArtist}"`);
        // Prevent infinite recursion by passing a flag or checking if already transliterated
        try {
            const response = await axios.get('https://ws.audioscrobbler.com/2.0/', {
                params: {
                    method: 'artist.getinfo',
                    artist: transArtist,
                    api_key: LASTFM_API_KEY,
                    format: 'json'
                },
                timeout: SEARCH_TIMEOUT
            });

            const artist = response.data.artist;
            if (artist && artist.image) {
                const images = artist.image;
                const targetImg = images.find(i => i.size === 'mega') || images.find(i => i.size === 'extralarge') || images[images.length - 1];

                if (targetImg && targetImg['#text']) {
                    const url = optimizeLastFMUrl(targetImg['#text']);
                    if (isValidArtwork(url)) {
                        console.log(`[Last.FM Artist Trans] Found: ${url}`);
                        return url;
                    }
                }
            }
        } catch (e) {
            console.warn('[Last.FM Artist Trans] Error:', e);
        }
    }

    return null;
};
